import React, { InputHTMLAttributes, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import Hint from 'components/styleguide/hint/Hint';
import Icon from 'components/styleguide/icon/Icon';
import IconButton from 'components/styleguide/icon_button/IconButton';
import MicroButton from 'components/styleguide/micro_button/MicroButton';
import { useAutoSave } from 'components/styleguide/hooks/useAutoSave';

const styles = require('./InputField.module.scss');

type NativeInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'size'>;

type InputSize = 'S' | 'M';
export type InputFieldContext = 'neutral' | 'talent' | 'company';

const SIZE_MODIFIER: Record<InputSize, string> = {
  S: 's',
  M: 'm',
};

const randomId = () => `input-field-${Math.random().toString(36).slice(2)}`;

const toString = (v: unknown): string => (v === undefined || v === null ? '' : String(v));

export interface InputFieldProps extends NativeInputProps {
  autoSaveUrl?: string;
  clearable?: boolean;
  containerClassName?: string;
  context?: InputFieldContext;
  disabled?: boolean;
  error?: string;
  hint?: string;
  iconLeft?: string;
  iconRight?: string;
  iconRightLabel?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  label?: string;
  onClear?: () => void;
  onIconRightClick?: () => void;
  placeholder?: string;
  required?: boolean;
  size?: InputSize;
}

const InputField: React.FC<InputFieldProps> = ({
  autoSaveUrl,
  clearable = true,
  containerClassName,
  context = 'neutral',
  disabled,
  error,
  hint,
  iconLeft,
  iconRight,
  iconRightLabel,
  id,
  inputRef,
  label,
  onClear,
  onIconRightClick,
  placeholder,
  required,
  size = 'S',
  type = 'text',
  ...inputProps
}) => {
  const { onChange, value, defaultValue, ...safeInputProps } = inputProps;
  const { autoSaveError, save } = useAutoSave({ url: autoSaveUrl || '', name: inputProps.name || '' });

  const [internalValue, setInternalValue] = useState<string>(toString(value ?? defaultValue));
  const [displayError, setDisplayError] = useState(error || '');
  const [inputId] = useState(() => id || randomId());
  const localRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => { setDisplayError(error || ''); }, [error]);
  useEffect(() => { if (autoSaveError) setDisplayError(autoSaveError); }, [autoSaveError]);
  useEffect(() => {
    if (value !== undefined) setInternalValue(toString(value));
  }, [value]);

  const setRef = (el: HTMLInputElement | null) => {
    localRef.current = el;
    if (typeof inputRef === 'function') inputRef(el);
    else if (inputRef) (inputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalValue(e.target.value);
    setDisplayError('');
    if (autoSaveUrl) save(e.target.value);
    onChange?.(e);
  };

  const handleClear = () => {
    setInternalValue('');
    setDisplayError('');
    if (autoSaveUrl) save('');
    onChange?.({
      target: { value: '' },
      currentTarget: { value: '' },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
    onClear?.();
    localRef.current?.focus();
  };

  const showHint = Boolean(hint) && !displayError;
  const hasMessage = Boolean(displayError) || showHint;
  const messageId = hasMessage ? `${inputId}-message` : undefined;
  const showClearButton = Boolean(clearable && internalValue && !disabled);

  const wrapperClasses = classNames(
    styles['ui-input-field'],
    styles[`ui-input-field--size-${SIZE_MODIFIER[size]}`],
    styles[`ui-input-field--context-${context}`],
    {
      [styles['ui-input-field--error']]: Boolean(displayError),
      [styles['ui-input-field--disabled']]: disabled,
    },
    containerClassName,
  );

  const iconColor = disabled ? 'neutral-border-disable' : 'neutral-border-darker';

  return (
    <div className={wrapperClasses}>
      {label && (
        <div className={styles['ui-input-field__label-row']}>
          <label htmlFor={inputId} className={styles['ui-input-field__label']}>{label}</label>
          {required && <span className={styles['ui-input-field__required']} aria-hidden="true">*</span>}
        </div>
      )}

      <label htmlFor={inputId} className={styles['ui-input-field__control']}>
        {iconLeft && (
          <span className={styles['ui-input-field__icon']}>
            <Icon name={iconLeft} size="sm" color={iconColor} aria-hidden="true" />
          </span>
        )}

        <input
          {...safeInputProps}
          id={inputId}
          type={type}
          className={styles['ui-input-field__input']}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          aria-invalid={Boolean(displayError)}
          aria-describedby={messageId}
          ref={setRef}
          value={internalValue}
          onChange={handleChange}
        />

        {showClearButton && (
          <MicroButton
            iconName="x"
            aria-label="Clear"
            onClick={handleClear}
          />
        )}

        {iconRight && (
          onIconRightClick ? (
            <IconButton
              iconName={iconRight}
              size="S"
              type="terceary"
              context="neutral"
              disabled={disabled}
              onClick={onIconRightClick}
              aria-label={iconRightLabel || 'Action'}
            />
          ) : (
            <span className={styles['ui-input-field__icon']}>
              <Icon name={iconRight} size="sm" color={iconColor} aria-hidden="true" />
            </span>
          )
        )}
      </label>

      {hasMessage && (
        <Hint
          id={messageId}
          variant={displayError ? 'error' : 'neutral'}
          message={displayError || hint}
        />
      )}
    </div>
  );
};

export default InputField;
